// src/pages/reports/FteDaysOffReport.tsx
//
// FTE & Requested Days Off — the planning view of what staff have asked for
// next year. Three ways in:
//   1. Planning summary  — school totals, weekday pressure, who asked for a
//                          part-time load without naming a day, load bands, and
//                          a per-learning-area breakdown.
//   2. Staff list        — every staff member, filterable and exportable.
//   3. Drilldown         — click anyone for their full answer in one place.

import React, { useEffect, useMemo, useState } from 'react';
import {
  UsersIcon,
  CalendarOffIcon,
  MessageCircleIcon,
  TrendingDownIcon,
  AlertTriangleIcon,
  SearchIcon,
  ScaleIcon,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import {
  getFteDaysOffReport,
  WEEKDAYS,
  type FteDaysOffReport as ReportData,
  type FteDaysOffRow,
  type Weekday,
} from '../../services/fteDaysOffReportService';
import { exportReport, type ExportData } from '../../utils/reportExport';
import { displayLearningArea } from '../../constants/learningAreas';
import { returnIntentionLabel, getIntentionStatusMeta } from '../../utils/intentionStats';

// Recharts needs literal colours, so these mirror the design tokens
// (src/styles/tokens.css) rather than Tailwind classes.
const NAVY = '#2E6BAC';
const WARN = '#C99213';

const WEEKDAY_FULL: Record<Weekday, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
};

const fmtFte = (v: number | null): string => (v == null ? '—' : v.toFixed(2));
const fmtDelta = (v: number | null): string =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}`;

type QuickFilter =
  | 'all'
  | 'reducing'
  | 'increasing'
  | 'days-off'
  | 'no-day-named'
  | 'discuss'
  | 'no-response'
  | 'leaving';

const QUICK_FILTERS: Array<{ id: QuickFilter; label: string }> = [
  { id: 'all', label: 'Everyone' },
  { id: 'days-off', label: 'Asked for days off' },
  { id: 'no-day-named', label: 'Part-time, no day named' },
  { id: 'reducing', label: 'Reducing load' },
  { id: 'increasing', label: 'Increasing load' },
  { id: 'discuss', label: 'Needs a conversation' },
  { id: 'leaving', label: 'Not returning / uncertain' },
  { id: 'no-response', label: 'No response' },
];

const FteDaysOffReport: React.FC = () => {
  const { addToast } = useToast();
  const { departments } = useData();

  const [year, setYear] = useState<number | null>(null);
  const [years, setYears] = useState<number[]>([]);
  const [department, setDepartment] = useState<string>('all');
  const [staffGroup, setStaffGroup] = useState<'teaching' | 'all'>('teaching');
  const [includeNonResponders, setIncludeNonResponders] = useState(true);

  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState<'summary' | 'list'>('summary');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<keyof FteDaysOffRow>('staffName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<FteDaysOffRow | null>(null);

  // Open on the year the school is actually collecting for: the open period,
  // else the most recent period, else next calendar year.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fallback = new Date().getFullYear() + 1;
      const found = new Set<number>();
      let resolved = fallback;
      try {
        const [open, all] = await Promise.all([
          intentionPeriodsService.getCurrentOpenPeriod(),
          intentionPeriodsService.getAllPeriods(),
        ]);
        all.forEach(p => p.academicYear && found.add(p.academicYear));
        if (open?.academicYear) resolved = open.academicYear;
        else if (found.size > 0) resolved = Math.max(...found);
      } catch (err) {
        console.error('Could not resolve intention years; using fallback:', err);
      }
      found.add(resolved);
      if (!cancelled) {
        setYears(Array.from(found).sort((a, b) => b - a));
        setYear(resolved);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await getFteDaysOffReport({
          year,
          department,
          staffGroup,
          includeNonResponders,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        console.error('FTE & days off report failed:', err);
        if (!cancelled) addToast('Failed to generate report', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, department, staffGroup, includeNonResponders, addToast]);

  // Stable identity so the derived lists below only recompute when the data does.
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const summary = data?.summary;

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(r => {
      if (term) {
        const haystack = `${r.staffName} ${r.staffCode} ${r.department}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      switch (quickFilter) {
        case 'reducing':
          return r.fteDelta != null && r.fteDelta < -0.001;
        case 'increasing':
          return r.fteDelta != null && r.fteDelta > 0.001;
        case 'days-off':
          return (r.daysOffEntitled ?? 0) > 0;
        case 'no-day-named':
          return r.awaitingDayChoice;
        case 'discuss':
          return r.needsDiscussion;
        case 'no-response':
          return !r.hasResponse;
        case 'leaving':
          return r.returning === 'no' || r.returning === 'uncertain';
        default:
          return true;
      }
    });
  }, [rows, search, quickFilter]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // unknowns last, both directions
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      return sortDir === 'desc'
        ? String(bv).localeCompare(String(av))
        : String(av).localeCompare(String(bv));
    });
    return copy;
  }, [filteredRows, sortKey, sortDir]);

  // Who named each weekday — the list a timetabler actually works from.
  // Submitted answers only, matching the summary totals (a draft isn't a response).
  const staffByWeekday = useMemo(() => {
    const map = new Map<Weekday, FteDaysOffRow[]>(WEEKDAYS.map(d => [d, []]));
    for (const row of rows) {
      if (!row.hasResponse) continue;
      for (const day of row.daysOffRequested) map.get(day)?.push(row);
    }
    return map;
  }, [rows]);

  // Days off owed but with no weekday against them yet.
  const unnamedRows = useMemo(
    () => rows.filter(r => r.hasResponse && r.daysOffUnnamed > 0),
    [rows]
  );

  // Asked for a part-time load and named no weekday at all. Heaviest reductions
  // first — three unplaced days is a bigger hole in the timetable than one.
  const noDayNamedRows = useMemo(
    () =>
      rows
        .filter(r => r.awaitingDayChoice)
        .sort(
          (a, b) =>
            (b.daysOffEntitled ?? 0) - (a.daysOffEntitled ?? 0) ||
            a.staffName.localeCompare(b.staffName)
        ),
    [rows]
  );

  // Named a day, but fewer than their requested load implies (e.g. 0.6 FTE with
  // only Wednesday against it).
  const partlyNamedRows = useMemo(
    () => rows.filter(r => r.hasResponse && r.daysOffNamed > 0 && r.daysOffUnnamed > 0),
    [rows]
  );

  const handleSort = (key: keyof FteDaysOffRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'staffName' || key === 'department' ? 'asc' : 'desc');
    }
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const exportData: ExportData = {
      title: `FTE and Requested Days Off ${year ?? ''}`,
      description: `Contracted FTE against the load and days off staff requested for ${year}`,
      metadata: {
        Generated: new Date().toLocaleDateString(),
        Year: String(year ?? ''),
        'Learning Area': department === 'all' ? 'All Learning Areas' : department,
        'Staff group': staffGroup === 'teaching' ? 'Teaching roles' : 'All active staff',
        Staff: String(sortedRows.length),
        Responded: `${summary?.respondedCount ?? 0} of ${summary?.staffCount ?? 0}`,
      },
      columns: [
        { header: 'Staff', key: 'staffName' },
        { header: 'Code', key: 'staffCode' },
        { header: 'Learning Area', key: 'department' },
        { header: 'Employment', key: 'employmentType' },
        { header: 'Current FTE', key: 'currentFte', format: fmtFte },
        { header: 'Requested FTE', key: 'requestedFte', format: fmtFte },
        { header: 'Requested load', key: 'fteLabel' },
        { header: 'Change', key: 'fteDelta', format: fmtDelta },
        {
          header: 'Days off requested',
          key: 'daysOffRequested',
          format: (v: Weekday[]) => (v && v.length ? v.join(' + ') : ''),
        },
        { header: 'Day-off answer', key: 'daysOffLabel' },
        { header: 'Days off per week', key: 'daysOffEntitled', format: v => (v == null ? '' : String(v)) },
        { header: 'Days not yet named', key: 'daysOffUnnamed' },
        {
          header: 'Part-time, no day named',
          key: 'awaitingDayChoice',
          format: v => (v ? 'Yes' : ''),
        },
        { header: 'Needs conversation', key: 'needsDiscussion', format: v => (v ? 'Yes' : '') },
        { header: 'Returning', key: 'returning', format: v => returnIntentionLabel(v) },
        { header: 'Leave planned', key: 'hasLeavePlans', format: v => (v ? 'Yes' : '') },
        { header: 'Leave type', key: 'leaveType', format: v => v ?? '' },
        { header: 'Leave from', key: 'leaveStartDate', format: v => v ?? '' },
        { header: 'Leave to', key: 'leaveEndDate', format: v => v ?? '' },
        {
          header: 'Submission',
          key: 'submissionStatus',
          format: v => (v ? getIntentionStatusMeta(v).label : 'No response'),
        },
      ],
      rows: sortedRows,
    };
    exportReport(exportData, format);
    addToast(`Report exported as ${format.toUpperCase()}`, 'success');
  };

  const weekdayChartData = useMemo(
    () =>
      (summary?.weekdayDemand ?? []).map(d => ({
        day: d.day,
        count: d.count,
      })),
    [summary]
  );

  const peakDay = useMemo(() => {
    if (!summary?.weekdayDemand?.length) return null;
    return summary.weekdayDemand.reduce((max, d) => (d.count > max.count ? d : max));
  }, [summary]);

  return (
    <div>
      <PageHeader
        title="FTE & Requested Days Off"
        description="What staff are contracted for now, the load they asked for next year, and which days they want off"
        actions={
          <>
            <select
              value={year ?? ''}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
              aria-label="Collection year"
            >
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
              aria-label="Learning area"
            >
              <option value="all">All Learning Areas</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={staffGroup}
              onChange={e => setStaffGroup(e.target.value as 'teaching' | 'all')}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
              aria-label="Staff group"
            >
              <option value="teaching">Teaching roles</option>
              <option value="all">All active staff</option>
            </select>
            <label className="inline-flex items-center text-body-sm text-ink-700 gap-2 px-2">
              <input
                type="checkbox"
                checked={includeNonResponders}
                onChange={e => setIncludeNonResponders(e.target.checked)}
              />
              Include non-responders
            </label>
            <select
              onChange={e => {
                if (e.target.value) {
                  handleExport(e.target.value as 'csv' | 'excel' | 'pdf');
                  e.target.value = '';
                }
              }}
              className="border border-ink-300 rounded-md px-3 py-2 bg-white text-body-sm"
              defaultValue=""
              aria-label="Export"
            >
              <option value="" disabled>
                Export
              </option>
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
              <option value="pdf">PDF</option>
            </select>
          </>
        }
      />

      {loading || !summary ? (
        <Card>
          <TableSkeleton rows={8} cols={7} />
        </Card>
      ) : summary.staffCount === 0 ? (
        <Card>
          {/* Three different reasons for an empty report, three different
              messages. The old copy always said "try a different filter", which
              is actively misleading when the school has no staff at all — that
              is what got a Marian College session escalated as a broken report
              (issue #152). */}
          {(data?.rosterSourceCount ?? 0) === 0 ? (
            <EmptyState
              icon={<UsersIcon className="h-6 w-6" />}
              title="No staff records for this school yet"
              description="Staff need to be imported before this report can show anything. Changing the filters will not help."
            />
          ) : (data?.groupMatchCount ?? 0) === 0 ? (
            <EmptyState
              icon={<UsersIcon className="h-6 w-6" />}
              title="No staff in the selected staff group"
              description={`${data?.rosterSourceCount ?? 0} staff on record, but none in this group. Switch the staff group to all active staff to include them.`}
            />
          ) : (
            <EmptyState
              icon={<UsersIcon className="h-6 w-6" />}
              title="No staff match the current filters"
              description={`${data?.groupMatchCount ?? 0} staff match the staff group, but none are in this learning area. Try a different learning area, or set it to all.`}
            />
          )}
        </Card>
      ) : (
        <>
          {/* Headline planning numbers */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <StatTile
              icon={<UsersIcon className="h-4 w-4" />}
              label="Responded"
              value={`${summary.respondedCount}/${summary.staffCount}`}
              hint={`${summary.noResponseCount} still to answer`}
            />
            <StatTile
              icon={<ScaleIcon className="h-4 w-4" />}
              label="FTE now"
              value={summary.currentFteTotal.toFixed(2)}
              hint="Contracted across these staff"
            />
            <StatTile
              icon={<ScaleIcon className="h-4 w-4" />}
              label="FTE requested"
              value={summary.requestedFteTotal.toFixed(2)}
              hint={`vs ${summary.comparableCurrentFteTotal.toFixed(2)} now for the same staff`}
            />
            <StatTile
              icon={<TrendingDownIcon className="h-4 w-4" />}
              label="Net change"
              value={fmtDelta(summary.fteDeltaTotal)}
              tone={summary.fteDeltaTotal < -0.001 ? 'danger' : summary.fteDeltaTotal > 0.001 ? 'ok' : 'neutral'}
              hint={`${summary.reducingCount} reducing · ${summary.increasingCount} increasing`}
            />
            <StatTile
              icon={<CalendarOffIcon className="h-4 w-4" />}
              label="No day named"
              value={String(summary.noDayNamedCount)}
              tone={summary.noDayNamedCount > 0 ? 'warn' : 'neutral'}
              hint={
                summary.noDayNamedCount > 0
                  ? `Part-time, ${summary.noDayNamedDaysTotal} day${
                      summary.noDayNamedDaysTotal === 1 ? '' : 's'
                    }/week unplaced`
                  : 'Every part-time request has a day'
              }
            />
            <StatTile
              icon={<MessageCircleIcon className="h-4 w-4" />}
              label="Need a conversation"
              value={String(summary.needsDiscussionCount)}
              tone={summary.needsDiscussionCount > 0 ? 'warn' : 'neutral'}
              hint="Chose “discuss” on load or day off"
            />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-ink-200 mb-4">
            {([
              { id: 'summary', label: 'Planning summary' },
              { id: 'list', label: `Staff list (${rows.length})` },
            ] as const).map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-body-sm font-medium border-b-2 ${
                  tab === t.id
                    ? 'text-navy-900 border-navy-900'
                    : 'text-ink-500 border-transparent hover:text-ink-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'summary' ? (
            <div className="space-y-6">
              {/* Weekday pressure */}
              <Card>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h2 className="text-section text-ink-900">Days off requested, by weekday</h2>
                    <p className="text-meta text-ink-500 mt-1">
                      How many staff asked for each day off. This is the constraint the timetable
                      has to absorb — a spike on one day is where classes get hard to cover.
                    </p>
                  </div>
                  {peakDay && peakDay.count > 0 && (
                    <Badge tone="navy">
                      Busiest: {WEEKDAY_FULL[peakDay.day]} ({peakDay.count})
                    </Badge>
                  )}
                </div>

                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weekdayChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: number) => [`${v} staff`, 'Requested off']} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {weekdayChartData.map(d => (
                          <Cell
                            key={d.day}
                            fill={peakDay && d.day === peakDay.day && d.count > 0 ? WARN : NAVY}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {summary.unnamedDaysOffTotal > 0 && (
                  <div className="mt-4 flex items-start gap-2 rounded-md bg-warn-bg border border-warn-border p-3">
                    <AlertTriangleIcon className="h-4 w-4 text-warn-fg flex-shrink-0 mt-0.5" />
                    <p className="text-body-sm text-warn-fg">
                      <strong className="font-mono tabular-nums">{summary.unnamedDaysOffTotal}</strong>{' '}
                      day{summary.unnamedDaysOffTotal === 1 ? '' : 's'} off per week
                      {' '}({unnamedRows.length} staff) have no weekday against them yet — they chose
                      “no preference”, asked to discuss, or skipped the question. Those days still
                      have to land somewhere — the people are named below.
                    </p>
                  </div>
                )}

                {/* Who asked for each day — the working list */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
                  {WEEKDAYS.map(day => {
                    const staff = staffByWeekday.get(day) ?? [];
                    return (
                      <div key={day} className="rounded-lg border border-ink-200 p-3">
                        <div className="flex items-baseline justify-between mb-2">
                          <h3 className="text-body-sm font-semibold text-ink-900">
                            {WEEKDAY_FULL[day]}
                          </h3>
                          <span className="text-body-sm font-mono tabular-nums text-navy-700">
                            {staff.length}
                          </span>
                        </div>
                        {staff.length === 0 ? (
                          <p className="text-meta text-ink-400">No requests</p>
                        ) : (
                          <ul className="space-y-1">
                            {staff.map(r => (
                              <li key={r.key}>
                                <button
                                  onClick={() => setSelected(r)}
                                  className="text-meta text-ink-700 hover:text-navy-700 hover:underline text-left"
                                >
                                  {r.staffName}{' '}
                                  <span className="font-mono tabular-nums text-ink-400">
                                    {fmtFte(r.requestedFte)}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Part-time with no day chosen — the follow-up list */}
              <Card>
                <div className="flex items-start justify-between gap-4 mb-1">
                  <div>
                    <h2 className="text-section text-ink-900">Part-time, no day chosen</h2>
                    <p className="text-meta text-ink-500 mt-1">
                      Staff who asked for a part-time load but named no weekday — “no preference”,
                      “wants to discuss”, or the question left blank. Their load is settled and
                      their day isn’t, so they’re the ones to go back to before the timetable is
                      built. Staff who asked to discuss their <em>load</em> aren’t here — their day
                      can’t be settled until the load is, and they’re counted under “Need a
                      conversation”.
                    </p>
                  </div>
                  {summary.noDayNamedCount > 0 && (
                    <Badge tone="warn">
                      {summary.noDayNamedDaysTotal} day
                      {summary.noDayNamedDaysTotal === 1 ? '' : 's'}/week unplaced
                    </Badge>
                  )}
                </div>

                {noDayNamedRows.length === 0 ? (
                  <p className="text-body-sm text-ink-700 mt-3">
                    Everyone who asked for a part-time load has named the day(s) they want off.
                  </p>
                ) : (
                  <div className="overflow-x-auto mt-4">
                    <table className="min-w-full divide-y divide-ink-200">
                      <thead className="bg-ink-50">
                        <tr>
                          <Th label="Staff" />
                          <Th label="Learning Area" />
                          <Th label="FTE now" align="right" />
                          <Th label="Requested" align="right" />
                          <Th label="Days off / week" align="right" />
                          <Th label="What they answered" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-ink-200">
                        {noDayNamedRows.map(r => (
                          <tr
                            key={r.key}
                            onClick={() => setSelected(r)}
                            className="hover:bg-ink-50 cursor-pointer"
                          >
                            <td className="px-4 py-2 text-body-sm text-ink-900">
                              {r.staffName}{' '}
                              {r.staffCode && (
                                <span className="text-ink-400 font-mono tabular-nums">
                                  ({r.staffCode})
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-body-sm text-ink-500">
                              {displayLearningArea(r.department)}
                            </td>
                            <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                              {fmtFte(r.currentFte)}
                            </td>
                            <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-navy-700 font-medium">
                              {fmtFte(r.requestedFte)}
                            </td>
                            <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                              {r.daysOffEntitled}
                            </td>
                            <td className="px-4 py-2 text-body-sm">
                              <Badge tone={r.dayOffPreference === 'discuss' ? 'warn' : 'neutral'}>
                                {r.dayOffPreference === 'discuss'
                                  ? 'Wants to discuss'
                                  : r.dayOffPreference === 'none'
                                  ? 'No preference'
                                  : 'Not answered'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-3 text-meta text-ink-500">
                      Click anyone for their full response. The same list is on the staff list tab
                      under “Part-time, no day named”.
                    </div>
                  </div>
                )}

                {partlyNamedRows.length > 0 && (
                  <div className="mt-4 border-t border-ink-200 pt-3">
                    <p className="text-meta text-ink-500">
                      A further {summary.partlyNamedCount} staff named fewer days than their requested
                      load implies:{' '}
                      {partlyNamedRows.map((r, i) => (
                        <React.Fragment key={r.key}>
                          {i > 0 && ', '}
                          <button
                            onClick={() => setSelected(r)}
                            className="text-ink-700 hover:text-navy-700 hover:underline"
                          >
                            {r.staffName}
                          </button>{' '}
                          <span className="font-mono tabular-nums text-ink-400">
                            ({r.daysOffRequested.join('+')} of {r.daysOffEntitled})
                          </span>
                        </React.Fragment>
                      ))}
                      .
                    </p>
                  </div>
                )}
              </Card>

              {/* Requested load bands */}
              <Card>
                <h2 className="text-section text-ink-900">Requested load</h2>
                <p className="text-meta text-ink-500 mt-1 mb-4">
                  What staff asked to work next year. “Wants to discuss” has no FTE attached yet, so
                  it sits outside the totals above.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <Th label="Requested load" />
                        <Th label="Staff" align="right" />
                        <Th label="Share" align="right" />
                        <Th label="FTE total" align="right" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {summary.fteBands.map(band => (
                        <tr key={band.preference} className="hover:bg-ink-50">
                          <td className="px-4 py-2 text-body-sm text-ink-900">{band.label}</td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {band.count}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-500">
                            {summary.respondedCount > 0
                              ? `${Math.round((band.count / summary.respondedCount) * 100)}%`
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {band.preference === 'discuss' ? '—' : band.fte.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {summary.noResponseCount > 0 && (
                        <tr className="bg-ink-50/50">
                          <td className="px-4 py-2 text-body-sm text-ink-500">No response</td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-500">
                            {summary.noResponseCount}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right text-ink-400">—</td>
                          <td className="px-4 py-2 text-body-sm text-right text-ink-400">—</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* By learning area */}
              <Card>
                <h2 className="text-section text-ink-900">By learning area</h2>
                <p className="text-meta text-ink-500 mt-1 mb-4">
                  Where the load is moving, and which areas carry the most days off to cover.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <Th label="Learning Area" />
                        <Th label="Staff" align="right" />
                        <Th label="Responded" align="right" />
                        <Th label="FTE now" align="right" />
                        <Th label="FTE requested" align="right" />
                        <Th label="Change" align="right" />
                        <Th label="Days off / week" align="right" />
                        <Th label="No day named" align="right" />
                        <Th label="To discuss" align="right" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {summary.byDepartment.map(d => (
                        <tr key={d.department} className="hover:bg-ink-50">
                          <td className="px-4 py-2 text-body-sm text-ink-900">
                            {displayLearningArea(d.department)}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {d.staffCount}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-500">
                            {d.respondedCount}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {d.currentFte.toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {d.requestedFte.toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums">
                            <DeltaText value={d.fteDelta} />
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {d.daysOffDemand}
                          </td>
                          <td
                            className={`px-4 py-2 text-body-sm text-right font-mono tabular-nums ${
                              d.noDayNamedCount > 0 ? 'text-warn-fg' : 'text-ink-500'
                            }`}
                          >
                            {d.noDayNamedCount || '—'}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-500">
                            {d.needsDiscussionCount || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-ink-50 border-t-2 border-ink-200">
                      <tr>
                        <td className="px-4 py-2 text-body-sm font-medium text-ink-900">Total</td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.staffCount}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.respondedCount}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.currentFteTotal.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.requestedFteTotal.toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-semibold font-mono tabular-nums">
                          <DeltaText value={summary.fteDeltaTotal} />
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.totalDaysOffRequested}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.noDayNamedCount || '—'}
                        </td>
                        <td className="px-4 py-2 text-body-sm text-right font-medium font-mono tabular-nums text-ink-900">
                          {summary.needsDiscussionCount || '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </div>
          ) : (
            <Card>
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search staff or code"
                    className="border border-ink-300 rounded-md pl-8 pr-3 py-2 text-body-sm w-56"
                  />
                </div>
                {QUICK_FILTERS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setQuickFilter(f.id)}
                    className={`rounded-full px-3 py-1 text-meta font-medium border ${
                      quickFilter === f.id
                        ? 'bg-navy-900 text-white border-navy-900'
                        : 'bg-white text-ink-700 border-ink-300 hover:bg-ink-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {sortedRows.length === 0 ? (
                <EmptyState
                  icon={<CalendarOffIcon className="h-6 w-6" />}
                  title="Nobody matches that filter"
                  description="Clear the search or pick a different filter to see staff again."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <Th label="Staff" onClick={() => handleSort('staffName')} />
                        <Th label="Learning Area" onClick={() => handleSort('department')} />
                        <Th label="FTE now" align="right" onClick={() => handleSort('currentFte')} />
                        <Th label="Requested" align="right" onClick={() => handleSort('requestedFte')} />
                        <Th label="Change" align="right" onClick={() => handleSort('fteDelta')} />
                        <Th label="Days off" onClick={() => handleSort('daysOffEntitled')} />
                        <Th label="Status" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {sortedRows.map(r => (
                        <tr
                          key={r.key}
                          onClick={() => setSelected(r)}
                          className="hover:bg-ink-50 cursor-pointer"
                        >
                          <td className="px-4 py-2 text-body-sm text-ink-900">
                            {r.staffName}{' '}
                            {r.staffCode && (
                              <span className="text-ink-400 font-mono tabular-nums">
                                ({r.staffCode})
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-ink-500">
                            {displayLearningArea(r.department)}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-ink-700">
                            {fmtFte(r.currentFte)}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums text-navy-700 font-medium">
                            {r.ftePreference === 'discuss' ? (
                              <span className="text-warn-fg font-sans">Discuss</span>
                            ) : (
                              fmtFte(r.requestedFte)
                            )}
                          </td>
                          <td className="px-4 py-2 text-body-sm text-right font-mono tabular-nums">
                            <DeltaText value={r.fteDelta} />
                          </td>
                          <td className="px-4 py-2 text-body-sm text-ink-700">
                            {r.daysOffRequested.length > 0 ? (
                              <span className="inline-flex gap-1">
                                {r.daysOffRequested.map(d => (
                                  <Badge key={d} tone="navy">
                                    {d}
                                  </Badge>
                                ))}
                                {r.daysOffUnnamed > 0 && (
                                  <Badge tone="warn">+{r.daysOffUnnamed} not named</Badge>
                                )}
                              </span>
                            ) : (r.daysOffEntitled ?? 0) > 0 ? (
                              <Badge tone="warn">
                                {r.daysOffEntitled} not named
                              </Badge>
                            ) : (
                              <span className="text-ink-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-body-sm">
                            <StatusBadge row={r} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 text-meta text-ink-500">
                    Showing {sortedRows.length} of {rows.length} staff · click a row for the full
                    response
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      <StaffDrilldown row={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
}> = ({ icon, label, value, hint, tone = 'neutral' }) => {
  const valueTone =
    tone === 'ok'
      ? 'text-ok-fg'
      : tone === 'warn'
      ? 'text-warn-fg'
      : tone === 'danger'
      ? 'text-danger-fg'
      : 'text-ink-900';
  return (
    <Card>
      <div className="flex items-center gap-1.5 text-meta text-ink-500">
        <span className="text-ink-400">{icon}</span>
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold font-mono tabular-nums ${valueTone}`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-meta text-ink-500">{hint}</div>}
    </Card>
  );
};

const DeltaText: React.FC<{ value: number | null }> = ({ value }) => {
  if (value == null) return <span className="text-ink-400">—</span>;
  const tone =
    value < -0.001 ? 'text-danger-fg' : value > 0.001 ? 'text-ok-fg' : 'text-ink-500';
  return <span className={tone}>{fmtDelta(value)}</span>;
};

const StatusBadge: React.FC<{ row: FteDaysOffRow }> = ({ row }) => {
  if (!row.hasResponse) {
    return <Badge tone={row.submissionStatus === 'draft' ? 'warn' : 'neutral'}>
      {row.submissionStatus === 'draft' ? 'Draft only' : 'No response'}
    </Badge>;
  }
  if (row.needsDiscussion) return <Badge tone="warn">Discuss</Badge>;
  if (row.returning === 'no') return <Badge tone="danger">Not returning</Badge>;
  if (row.returning === 'uncertain') return <Badge tone="warn">Uncertain</Badge>;
  const meta = getIntentionStatusMeta(row.submissionStatus);
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
};

const Th: React.FC<{
  label: string;
  onClick?: () => void;
  align?: 'left' | 'right';
}> = ({ label, onClick, align = 'left' }) => (
  <th
    onClick={onClick}
    className={`px-4 py-2 text-meta font-medium text-ink-500 uppercase tracking-wider ${
      align === 'right' ? 'text-right' : 'text-left'
    } ${onClick ? 'cursor-pointer select-none' : ''}`}
  >
    {label}
  </th>
);

/** Everything one staff member said, in one place. */
const StaffDrilldown: React.FC<{ row: FteDaysOffRow | null; onClose: () => void }> = ({
  row,
  onClose,
}) => {
  if (!row) return null;
  return (
    <Modal
      isOpen={!!row}
      onClose={onClose}
      size="lg"
      title={
        <span>
          {row.staffName}{' '}
          {row.staffCode && (
            <span className="text-ink-400 font-mono tabular-nums">({row.staffCode})</span>
          )}
        </span>
      }
      description={`${displayLearningArea(row.department)} · ${row.employmentType}`}
    >
      <div className="space-y-5">
        {row.needsDiscussion && (
          <div className="flex items-start gap-2 rounded-md bg-warn-bg border border-warn-border p-3">
            <MessageCircleIcon className="h-4 w-4 text-warn-fg flex-shrink-0 mt-0.5" />
            <p className="text-body-sm text-warn-fg">
              Asked to discuss their options — settle this before the load is planned.
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <Field label="FTE now" value={fmtFte(row.currentFte)} mono />
          <Field
            label="Requested"
            value={row.ftePreference === 'discuss' ? 'Discuss' : fmtFte(row.requestedFte)}
            mono={row.ftePreference !== 'discuss'}
          />
          <Field label="Change" value={fmtDelta(row.fteDelta)} mono />
        </div>

        <div>
          <div className="text-meta text-ink-500 uppercase tracking-wider mb-1.5">Days off</div>
          {row.daysOffRequested.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {row.daysOffRequested.map(d => (
                <Badge key={d} tone="navy">
                  {WEEKDAY_FULL[d]}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-body-sm text-ink-700">{row.daysOffLabel}</p>
          )}
          <p className="text-meta text-ink-500 mt-1.5">
            {row.daysOffEntitled == null
              ? 'Days off per week depend on the load conversation.'
              : `${row.daysOffEntitled} day${row.daysOffEntitled === 1 ? '' : 's'} off per week at the requested load` +
                (row.daysOffUnnamed > 0
                  ? ` · ${row.daysOffUnnamed} not yet assigned to a weekday`
                  : '')}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Returning next year" value={returnIntentionLabel(row.returning)} />
          <Field
            label="Leave planned"
            value={
              row.hasLeavePlans
                ? [row.leaveType, row.leaveStartDate && `from ${row.leaveStartDate}`, row.leaveEndDate && `to ${row.leaveEndDate}`]
                    .filter(Boolean)
                    .join(' ') || 'Yes'
                : 'None recorded'
            }
          />
        </div>

        {row.leavePlans && (
          <Field label="Leave / sabbatical notes" value={row.leavePlans} block />
        )}
        {row.comments && <Field label="Their comments" value={row.comments} block />}

        <div className="grid grid-cols-2 gap-4 border-t border-ink-200 pt-4">
          <Field
            label="Submission"
            value={
              row.hasResponse
                ? getIntentionStatusMeta(row.submissionStatus).label
                : row.submissionStatus === 'draft'
                ? 'Draft only — not submitted'
                : 'No response'
            }
          />
          <Field
            label="Submitted"
            value={
              row.submittedAt
                ? `${row.submittedAt.toLocaleDateString()}${row.isLate ? ' (late)' : ''}`
                : '—'
            }
          />
        </div>
      </div>
    </Modal>
  );
};

const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  block?: boolean;
}> = ({ label, value, mono, block }) => (
  <div className={block ? 'w-full' : ''}>
    <div className="text-meta text-ink-500 uppercase tracking-wider mb-1">{label}</div>
    <div
      className={`text-body-sm text-ink-900 ${mono ? 'font-mono tabular-nums' : ''} ${
        block ? 'whitespace-pre-wrap' : ''
      }`}
    >
      {value}
    </div>
  </div>
);

export default FteDaysOffReport;
